/**
 * WB4 Depth — Phase D: Role cockpit configuration.
 * Zone visibility / density / signals — constants are fallback defaults; tenant config merges via server fetch.
 * Layout composition → `workbench-role-composition-registry.ts` (pilot).
 */

import type { UserRole } from '@shared/types/roles';
import { ROLES } from '@shared/types/roles';
import type { WorkbenchDensity } from './density-governance';
import type { OperationalIntent } from './workbench-intent';
import type { NavigatorTabKey } from './workbench-role-cockpit';
import {
  formatInspectorSignalRawValue,
  readInspectorSignalObjectField,
} from './workbench-inspector-signal-field-contract';

export type WorkbenchRole = 'sales' | 'finance' | 'warehouse' | 'admin' | 'operations' | 'owner';

export interface WorkbenchRoleContext {
  userId: string;
  role: WorkbenchRole;
  roleGroups: string[];
  permissions: string[];
  tenantId: string;
}

export type RoleNavigatorQueueSource =
  | 'exception'
  | 'saved_view'
  | 'query'
  | 'flow_queue'
  | 'task_queue'
  | 'audit_flag';

export interface RoleNavigatorQueueConfig {
  id: string;
  label: string;
  description?: string;
  domain: string;
  objectType?: string;
  source: RoleNavigatorQueueSource;
  filter: Record<string, unknown>;
  badge?: {
    countSource: string;
    severity?: 'info' | 'warning' | 'danger';
  };
  defaultAction?: string;
}

export interface RoleNavigatorConfig {
  role: WorkbenchRole;
  defaultLanding: {
    domain: string;
    surface: 'list' | 'dashboard' | 'queue';
    viewId?: string;
  };
  queues: RoleNavigatorQueueConfig[];
  navigatorDefaultTab?: NavigatorTabKey;
}

export type InspectorSignalSeverity = 'info' | 'success' | 'warning' | 'danger';

export interface InspectorSignal {
  id: string;
  label: string;
  value?: string | number | undefined;
  severity: InspectorSignalSeverity;
  actionId?: string | undefined;
  visible: boolean;
}

export interface RoleWorkbenchPreset {
  role: WorkbenchRole;
  density: WorkbenchDensity;
  zones: {
    navigatorVisible: boolean;
    inspectorVisible: boolean;
    auxiliaryVisible: boolean;
    navigatorWidth?: number;
    inspectorWidth?: number;
    auxiliaryHeight?: number;
  };
  listDefaults: {
    defaultViewId?: string;
    visibleColumns?: string[];
    hiddenColumns?: string[];
    defaultFilters?: Record<string, unknown>;
    sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  };
  inspectorDefaults: {
    signalPriority: string[];
    quickActions: string[];
    defaultTab?: 'flows' | 'advanced';
    aiLayersInInspector?: boolean;
  };
  runtimeIntent?: OperationalIntent;
  auxiliaryTabOrder?: readonly string[];
}

export type ResolvedRoleWorkbenchPreset = RoleWorkbenchPreset;

export interface TenantRoleConfigPartial {
  navigator?: Partial<RoleNavigatorConfig>;
  preset?: Partial<RoleWorkbenchPreset>;
}

const USER_ROLE_TO_WORKBENCH_ROLE: Partial<Record<UserRole, WorkbenchRole>> = {
  [ROLES.SALES]: 'sales',
  [ROLES.ACCOUNTING]: 'finance',
  [ROLES.OPERATIONS_MANAGER]: 'warehouse',
  [ROLES.HR_ADMIN]: 'admin',
  [ROLES.OWNER]: 'owner',
  [ROLES.EXTERNAL]: 'sales',
};

export const SALES_NAVIGATOR_CONFIG: RoleNavigatorConfig = {
  role: 'sales',
  defaultLanding: { domain: 'customer', surface: 'queue' },
  navigatorDefaultTab: 'queue',
  queues: [
    {
      id: 'sales-ar-aging',
      label: '수금 지연 고객',
      domain: 'customer',
      source: 'query',
      filter: { arAgingDays: { gte: 15 } },
      badge: { countSource: 'ar-aging', severity: 'warning' },
      defaultAction: 'customer:open_collection_thread',
    },
    {
      id: 'sales-credit-warning',
      label: '여신 주의',
      domain: 'customer',
      source: 'exception',
      filter: { exceptionType: 'credit_warning' },
      badge: { countSource: 'credit-warning', severity: 'danger' },
    },
  ],
};

export const WAREHOUSE_NAVIGATOR_CONFIG: RoleNavigatorConfig = {
  role: 'warehouse',
  defaultLanding: { domain: 'inventory', surface: 'queue' },
  navigatorDefaultTab: 'exceptions',
  queues: [
    {
      id: 'warehouse-picking',
      label: '피킹 대기',
      domain: 'order',
      source: 'task_queue',
      filter: { taskType: 'picking', status: 'pending' },
      badge: { countSource: 'picking-pending' },
      defaultAction: 'workbench:prepare-delivery',
    },
    {
      id: 'warehouse-low-stock',
      label: '재고 부족',
      domain: 'inventory',
      source: 'exception',
      filter: { exceptionType: 'low_stock' },
      badge: { countSource: 'low-stock', severity: 'warning' },
    },
  ],
};

export const FINANCE_NAVIGATOR_CONFIG: RoleNavigatorConfig = {
  role: 'finance',
  defaultLanding: { domain: 'finance', surface: 'queue' },
  navigatorDefaultTab: 'exceptions',
  queues: [
    {
      id: 'finance-overdue-receivables',
      label: '연체 채권',
      domain: 'finance',
      source: 'exception',
      filter: { exceptionType: 'overdue_receivable' },
      badge: { countSource: 'overdue-ar', severity: 'danger' },
      defaultAction: 'finance:collect',
    },
    {
      id: 'finance-correction-approver',
      label: '보정 승인 대기',
      description: '내가 승인해야 할 고위험 보정 요청',
      domain: 'finance',
      source: 'task_queue',
      filter: { taskType: 'correction_approval' },
      badge: { countSource: 'correction-approver-pending', severity: 'warning' },
      defaultAction: 'workbench:correction-approver-inbox',
    },
    {
      id: 'finance-pending-approvals',
      label: '승인 대기 배정',
      description: '승인 대기 중인 업무 배정',
      domain: 'flow',
      source: 'task_queue',
      filter: { taskType: 'approval_pending' },
      badge: { countSource: 'pending-approvals', severity: 'warning' },
    },
  ],
};

export const ADMIN_NAVIGATOR_CONFIG: RoleNavigatorConfig = {
  role: 'admin',
  defaultLanding: { domain: 'entity', surface: 'dashboard' },
  navigatorDefaultTab: 'queue',
  queues: [
    {
      id: 'admin-permission-issues',
      label: '권한 이슈',
      domain: 'entity',
      source: 'audit_flag',
      filter: { flagType: 'permission_issue' },
      badge: { countSource: 'permission-issues', severity: 'warning' },
    },
    {
      id: 'admin-correction-approver',
      label: '보정 승인 대기',
      description: '내가 승인해야 할 고위험 보정 요청',
      domain: 'entity',
      source: 'task_queue',
      filter: { taskType: 'correction_approval' },
      badge: { countSource: 'correction-approver-pending', severity: 'warning' },
      defaultAction: 'workbench:correction-approver-inbox',
    },
    {
      id: 'admin-failed-actions',
      label: '실패한 작업',
      description: '검증 실패·실행 오류로 중단된 워크벤치 작업',
      domain: 'entity',
      source: 'audit_flag',
      filter: { flagType: 'failed_action' },
      badge: { countSource: 'failed-actions', severity: 'danger' },
    },
  ],
};

export const OWNER_NAVIGATOR_CONFIG: RoleNavigatorConfig = {
  role: 'owner',
  defaultLanding: { domain: 'finance', surface: 'dashboard' },
  navigatorDefaultTab: 'exceptions',
  queues: [
    {
      id: 'owner-credit-hold',
      label: '신용 보류',
      domain: 'customer',
      source: 'exception',
      filter: { exceptionType: 'credit_hold' },
      badge: { countSource: 'credit-hold', severity: 'danger' },
    },
  ],
};

/** Fallback-only defaults — server tenant config merges on top. */
export const DEFAULT_ROLE_CONFIGS: Readonly<Record<WorkbenchRole, RoleNavigatorConfig>> = {
  sales: SALES_NAVIGATOR_CONFIG,
  finance: FINANCE_NAVIGATOR_CONFIG,
  warehouse: WAREHOUSE_NAVIGATOR_CONFIG,
  admin: ADMIN_NAVIGATOR_CONFIG,
  owner: OWNER_NAVIGATOR_CONFIG,
  operations: WAREHOUSE_NAVIGATOR_CONFIG,
};

export const SYSTEM_ROLE_WORKBENCH_PRESET: Omit<RoleWorkbenchPreset, 'role'> = {
  density: 'standard',
  zones: {
    navigatorVisible: true,
    inspectorVisible: true,
    auxiliaryVisible: true,
  },
  listDefaults: {},
  inspectorDefaults: {
    signalPriority: [],
    quickActions: [],
    defaultTab: 'flows',
    aiLayersInInspector: false,
  },
};

/** Personal settings quick action ids — must resolve in RowInspectorT0 ROLE_QUICK_ACTIONS. */
export const PERSONAL_INSPECTOR_QUICK_ACTION_IDS = [
  'order:create',
  'customer:open_collection_thread',
  'finance:collect',
  'export',
  'inventory:receive',
  'task:complete',
  'assign_role',
  'review_permissions',
] as const;

export const PERSONAL_INSPECTOR_QUICK_ACTION_LABELS: Readonly<
  Record<(typeof PERSONAL_INSPECTOR_QUICK_ACTION_IDS)[number], string>
> = {
  'order:create': '신규 주문 처리',
  'customer:open_collection_thread': '수금 Thread 열기',
  'finance:collect': '수금 처리',
  export: '보내기',
  'inventory:receive': '재고 입고',
  'task:complete': '작업 완료',
  assign_role: '역할 배정',
  review_permissions: '권한 검토',
};

export const DEFAULT_ROLE_PRESETS: Readonly<Record<WorkbenchRole, RoleWorkbenchPreset>> = {
  sales: {
    role: 'sales',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    density: 'comfort',
    inspectorDefaults: {
      signalPriority: [
        'ar_aging',
        'credit_warning',
        'last_order',
        'collection_next',
        'linked_customer',
        'schedule_variance',
        'overdue_delivery',
        'fulfillment_stage',
        'delivery_evidence_missing',
        'production_queue',
      ],
      quickActions: ['order:create', 'customer:open_collection_thread'],
      defaultTab: 'flows',
    },
  },
  finance: {
    role: 'finance',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    zones: {
      ...SYSTEM_ROLE_WORKBENCH_PRESET.zones,
      navigatorWidth: 260,
      inspectorWidth: 420,
      auxiliaryHeight: 200,
    },
    density: 'standard',
    inspectorDefaults: {
      signalPriority: [
        'credit_limit',
        'overdue_amount',
        'payment_behavior',
        'approval_flags',
        'schedule_variance',
        'overdue_delivery',
        'delivery_evidence_missing',
      ],
      quickActions: ['finance:collect', 'export'],
      defaultTab: 'advanced',
    },
  },
  warehouse: {
    role: 'warehouse',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    zones: {
      ...SYSTEM_ROLE_WORKBENCH_PRESET.zones,
      navigatorWidth: 300,
      inspectorWidth: 280,
      auxiliaryHeight: 240,
    },
    density: 'standard',
    inspectorDefaults: {
      signalPriority: [
        'picking_status',
        'picking_availability',
        'dispatch_queue',
        'stock_link',
        'low_stock',
        'reserved_quantity',
      ],
      quickActions: ['inventory:receive', 'task:complete'],
      defaultTab: 'flows',
    },
  },
  admin: {
    role: 'admin',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    density: 'standard',
    inspectorDefaults: {
      signalPriority: ['permission_issues', 'audit_flags', 'schema_changes'],
      quickActions: ['assign_role'],
      defaultTab: 'flows',
    },
  },
  owner: {
    role: 'owner',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    zones: {
      ...SYSTEM_ROLE_WORKBENCH_PRESET.zones,
      navigatorWidth: 260,
      inspectorWidth: 420,
      auxiliaryHeight: 200,
    },
    density: 'expert',
    inspectorDefaults: {
      signalPriority: ['credit_hold', 'overdue_assignments', 'audit_flags'],
      quickActions: ['review_permissions'],
      defaultTab: 'advanced',
      aiLayersInInspector: true,
    },
  },
  operations: {
    role: 'operations',
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    zones: {
      ...SYSTEM_ROLE_WORKBENCH_PRESET.zones,
      navigatorWidth: 300,
      inspectorWidth: 280,
      auxiliaryHeight: 240,
    },
    density: 'standard',
    inspectorDefaults: {
      signalPriority: ['missing_evidence_tasks', 'overdue_assignments'],
      quickActions: ['task:complete'],
      defaultTab: 'flows',
    },
  },
};

const ROLE_SIGNAL_PRIORITY: Readonly<Record<WorkbenchRole, readonly string[]>> = {
  sales: [
    'ar_aging',
    'credit_warning',
    'last_order',
    'collection_next',
    'linked_customer',
    'schedule_variance',
    'overdue_delivery',
    'fulfillment_stage',
    'delivery_evidence_missing',
    'production_queue',
  ],
  finance: [
    'credit_limit',
    'overdue_amount',
    'payment_behavior',
    'approval_flags',
    'schedule_variance',
    'overdue_delivery',
    'delivery_evidence_missing',
  ],
  warehouse: [
    'picking_status',
    'picking_availability',
    'dispatch_queue',
    'stock_link',
    'low_stock',
    'reserved_quantity',
  ],
  admin: ['permission_issues', 'audit_flags', 'schema_changes'],
  owner: ['credit_hold', 'overdue_assignments', 'audit_flags'],
  operations: ['missing_evidence_tasks', 'overdue_assignments', 'stock_link'],
};

export function resolveWorkbenchRoleFromUserRole(userRole: UserRole): WorkbenchRole {
  return USER_ROLE_TO_WORKBENCH_ROLE[userRole] ?? 'sales';
}

export function isWorkbenchRole(value: unknown): value is WorkbenchRole {
  return (
    value === 'sales' ||
    value === 'finance' ||
    value === 'warehouse' ||
    value === 'admin' ||
    value === 'operations' ||
    value === 'owner'
  );
}

function mergeQueues(
  defaults: readonly RoleNavigatorQueueConfig[],
  overrides: readonly RoleNavigatorQueueConfig[] | undefined
): RoleNavigatorQueueConfig[] {
  if (!overrides || overrides.length === 0) {
    return [...defaults];
  }

  const merged = new Map(defaults.map((queue) => [queue.id, { ...queue }]));
  for (const override of overrides) {
    const existing = merged.get(override.id);
    merged.set(override.id, existing ? { ...existing, ...override } : { ...override });
  }
  return [...merged.values()];
}

/** Merge tenant server config onto static fallback defaults. */
export function mergeRoleNavigatorConfig(
  defaultConfig: RoleNavigatorConfig,
  tenantConfig: Partial<RoleNavigatorConfig> | null | undefined
): RoleNavigatorConfig {
  if (!tenantConfig) {
    return { ...defaultConfig, queues: [...defaultConfig.queues] };
  }

  return {
    ...defaultConfig,
    ...tenantConfig,
    role: defaultConfig.role,
    defaultLanding: {
      ...defaultConfig.defaultLanding,
      ...tenantConfig.defaultLanding,
    },
    queues: mergeQueues(defaultConfig.queues, tenantConfig.queues),
  };
}

export function resolveRoleNavigatorConfig(
  role: WorkbenchRole,
  tenantConfig?: Partial<RoleNavigatorConfig> | null
): RoleNavigatorConfig {
  const defaultConfig = DEFAULT_ROLE_CONFIGS[role] ?? DEFAULT_ROLE_CONFIGS.sales;
  return mergeRoleNavigatorConfig(defaultConfig, tenantConfig);
}

export function resolveRoleNavigatorQueues(
  role: WorkbenchRole,
  tenantConfig?: Partial<RoleNavigatorConfig> | null
): RoleNavigatorQueueConfig[] {
  return resolveRoleNavigatorConfig(role, tenantConfig).queues;
}

function mergePresetLayer(
  base: RoleWorkbenchPreset,
  layer: Partial<RoleWorkbenchPreset> | undefined
): RoleWorkbenchPreset {
  if (!layer) {
    return base;
  }

  return {
    ...base,
    ...layer,
    role: base.role,
    zones: { ...base.zones, ...layer.zones },
    listDefaults: { ...base.listDefaults, ...layer.listDefaults },
    inspectorDefaults: { ...base.inspectorDefaults, ...layer.inspectorDefaults },
    ...(layer.auxiliaryTabOrder ? { auxiliaryTabOrder: [...layer.auxiliaryTabOrder] } : {}),
    ...(layer.runtimeIntent ? { runtimeIntent: layer.runtimeIntent } : {}),
  };
}

/** Preset merge order — later layers override earlier layers. */
export const ROLE_WORKBENCH_PRESET_LAYER_ORDER = [
  'system',
  'role',
  'workspace',
  'personal',
  'runtime',
] as const;

/**
 * Apply order: system → role → workspace → personal → runtime.
 * Later layers override earlier layers.
 */
export function applyRoleWorkbenchPresetLayers(input: {
  role: WorkbenchRole;
  workspace?: Partial<RoleWorkbenchPreset>;
  personal?: Partial<RoleWorkbenchPreset>;
  runtime?: Partial<RoleWorkbenchPreset>;
}): ResolvedRoleWorkbenchPreset {
  const rolePreset = DEFAULT_ROLE_PRESETS[input.role] ?? DEFAULT_ROLE_PRESETS.sales;
  const systemBaseline: RoleWorkbenchPreset = {
    role: input.role,
    ...SYSTEM_ROLE_WORKBENCH_PRESET,
    inspectorDefaults: { ...SYSTEM_ROLE_WORKBENCH_PRESET.inspectorDefaults },
  };

  return [
    systemBaseline,
    rolePreset,
    input.workspace,
    input.personal,
    input.runtime,
  ].reduce<RoleWorkbenchPreset>(
    (current, layer) => mergePresetLayer(current, layer),
    systemBaseline
  );
}

/**
 * Apply personal (and optional runtime) overrides on an already-resolved preset
 * (e.g. server-merged system→role→workspace). Preserves layer order: workspace → personal → runtime.
 */
export function applyRoleWorkbenchPresetOverrides(
  base: RoleWorkbenchPreset,
  overrides: {
    workspace?: Partial<RoleWorkbenchPreset>;
    personal?: Partial<RoleWorkbenchPreset>;
    runtime?: Partial<RoleWorkbenchPreset>;
  }
): ResolvedRoleWorkbenchPreset {
  return [overrides.workspace, overrides.personal, overrides.runtime].reduce<RoleWorkbenchPreset>(
    (current, layer) => mergePresetLayer(current, layer),
    base
  );
}

function readObjectField(
  object: Record<string, unknown>,
  keys: string | readonly string[]
): unknown {
  return readInspectorSignalObjectField(object, keys);
}

function mergeRoleSignalsById(
  primary: readonly InspectorSignal[],
  secondary: readonly InspectorSignal[]
): InspectorSignal[] {
  const merged = new Map(primary.map((signal) => [signal.id, signal]));
  for (const signal of secondary) {
    if (!merged.has(signal.id)) {
      merged.set(signal.id, signal);
    }
  }
  return [...merged.values()];
}

function formatSignalValue(value: unknown): string | number | undefined {
  const formatted = formatInspectorSignalRawValue(value);
  if (formatted !== undefined) {
    return formatted;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return undefined;
}

function buildSalesCustomerSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'ar_aging',
      label: 'AR 연체',
      value: formatSignalValue(readObjectField(object, 'arAgingDays')),
      severity: 'warning',
      actionId: 'customer:open_collection_thread',
      visible: true,
    },
    {
      id: 'credit_warning',
      label: '여신 주의',
      value: formatSignalValue(readObjectField(object, 'creditWarning')),
      severity: 'danger',
      visible: Boolean(readObjectField(object, 'creditWarning')),
    },
    {
      id: 'last_order',
      label: '최근 주문',
      value: formatSignalValue(readObjectField(object, 'lastOrderAt')),
      severity: 'info',
      visible: readObjectField(object, 'lastOrderAt') !== undefined,
    },
    {
      id: 'collection_next',
      label: '다음 수금 액션',
      value: formatSignalValue(readObjectField(object, 'collectionNextAction')),
      severity: 'info',
      actionId: 'customer:open_collection_thread',
      visible: readObjectField(object, 'collectionNextAction') !== undefined,
    },
  ];
}

function buildFinanceCustomerSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'credit_limit',
      label: '여신 한도',
      value: formatSignalValue(readObjectField(object, 'creditLimit')),
      severity: 'info',
      visible: readObjectField(object, 'creditLimit') !== undefined,
    },
    {
      id: 'overdue_amount',
      label: '연체 금액',
      value: formatSignalValue(readObjectField(object, 'overdueAmount')),
      severity: 'danger',
      visible: Number(readObjectField(object, 'overdueAmount') ?? 0) > 0,
    },
    {
      id: 'payment_behavior',
      label: '결제 성향',
      value: formatSignalValue(readObjectField(object, 'paymentBehavior')),
      severity: 'warning',
      visible: readObjectField(object, 'paymentBehavior') !== undefined,
    },
    {
      id: 'approval_flags',
      label: '승인 플래그',
      value: formatSignalValue(readObjectField(object, 'approvalFlags')),
      severity: 'warning',
      visible: readObjectField(object, 'approvalFlags') !== undefined,
    },
  ];
}

function buildWarehouseInventorySignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'stock_link',
      label: '재고 연결',
      value: formatSignalValue(readObjectField(object, 'stockLink')),
      severity: 'info',
      visible: readObjectField(object, 'stockLink') !== undefined,
    },
    {
      id: 'reserved_quantity',
      label: '예약 수량',
      value: formatSignalValue(readObjectField(object, 'reservedQuantity')),
      severity: 'warning',
      visible: readObjectField(object, 'reservedQuantity') !== undefined,
    },
    {
      id: 'low_stock',
      label: '재고 부족',
      value: formatSignalValue(readObjectField(object, 'lowStock')),
      severity: 'danger',
      visible: Boolean(readObjectField(object, 'lowStock')),
    },
  ];
}

function buildWarehouseOrderSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'picking_status',
      label: '피킹 상태',
      value: formatSignalValue(readObjectField(object, 'pickingStatus')),
      severity: 'info',
      visible: readObjectField(object, 'pickingStatus') !== undefined,
    },
    {
      id: 'picking_availability',
      label: '피킹 가능',
      value: formatSignalValue(readObjectField(object, 'pickingAvailable')),
      severity: 'success',
      visible: readObjectField(object, 'pickingAvailable') !== undefined,
    },
    {
      id: 'dispatch_queue',
      label: '출고 대기열',
      value: formatSignalValue(readObjectField(object, 'dispatchQueue')),
      severity: 'warning',
      visible: readObjectField(object, 'dispatchQueue') !== undefined,
    },
  ];
}

function buildSalesOrderSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'linked_customer',
      label: '연결 고객',
      value: formatSignalValue(
        readObjectField(object, ['linkedCustomer', 'linked_customer', 'customerName'])
      ),
      severity: 'info',
      visible:
        readObjectField(object, ['linkedCustomer', 'linked_customer', 'customerName']) !==
        undefined,
    },
    {
      id: 'schedule_variance',
      label: '일정 편차',
      value: formatSignalValue(readObjectField(object, ['scheduleVariance', 'schedule_variance'])),
      severity: 'warning',
      visible: readObjectField(object, ['scheduleVariance', 'schedule_variance']) !== undefined,
    },
    {
      id: 'overdue_delivery',
      label: '납기 초과',
      value: formatSignalValue(
        readObjectField(object, ['overdueDelivery', 'overdue_delivery', 'deliveryOverdueDays'])
      ),
      severity: 'danger',
      visible:
        Boolean(readObjectField(object, ['overdueDelivery', 'overdue_delivery'])) ||
        Number(readObjectField(object, ['deliveryOverdueDays', 'delivery_overdue_days']) ?? 0) > 0,
    },
    {
      id: 'fulfillment_stage',
      label: '이행 단계',
      value: formatSignalValue(readObjectField(object, ['fulfillmentStage', 'fulfillment_stage'])),
      severity: 'warning',
      visible: readObjectField(object, ['fulfillmentStage', 'fulfillment_stage']) !== undefined,
    },
    {
      id: 'delivery_evidence_missing',
      label: '납품 증빙 누락',
      value: formatSignalValue(
        readObjectField(object, [
          'deliveryEvidenceMissing',
          'delivery_evidence_missing',
          'missingDeliveryEvidence',
          'missingDeliveryEvidenceCount',
        ])
      ),
      severity: 'warning',
      actionId: 'task:complete',
      visible:
        Boolean(
          readObjectField(object, [
            'deliveryEvidenceMissing',
            'delivery_evidence_missing',
            'missingDeliveryEvidence',
          ])
        ) ||
        Number(
          readObjectField(object, [
            'missingDeliveryEvidenceCount',
            'missing_delivery_evidence_count',
          ]) ?? 0
        ) > 0,
    },
    {
      id: 'production_queue',
      label: '생산 대기열',
      value: formatSignalValue(readObjectField(object, ['productionQueue', 'production_queue'])),
      severity: 'info',
      visible: readObjectField(object, ['productionQueue', 'production_queue']) !== undefined,
    },
  ];
}

function buildFinanceOrderSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'overdue_amount',
      label: '연체 금액',
      value: formatSignalValue(readObjectField(object, ['overdueAmount', 'overdue_amount'])),
      severity: 'danger',
      visible: Number(readObjectField(object, ['overdueAmount', 'overdue_amount']) ?? 0) > 0,
    },
    {
      id: 'approval_flags',
      label: '승인 플래그',
      value: formatSignalValue(readObjectField(object, ['approvalFlags', 'approval_flags'])),
      severity: 'warning',
      visible: readObjectField(object, ['approvalFlags', 'approval_flags']) !== undefined,
    },
    {
      id: 'schedule_variance',
      label: '일정 편차',
      value: formatSignalValue(readObjectField(object, ['scheduleVariance', 'schedule_variance'])),
      severity: 'warning',
      visible: readObjectField(object, ['scheduleVariance', 'schedule_variance']) !== undefined,
    },
    {
      id: 'overdue_delivery',
      label: '납기 초과',
      value: formatSignalValue(
        readObjectField(object, ['overdueDelivery', 'overdue_delivery', 'deliveryOverdueDays'])
      ),
      severity: 'danger',
      visible:
        Boolean(readObjectField(object, ['overdueDelivery', 'overdue_delivery'])) ||
        Number(readObjectField(object, ['deliveryOverdueDays', 'delivery_overdue_days']) ?? 0) > 0,
    },
    {
      id: 'delivery_evidence_missing',
      label: '납품 증빙 누락',
      value: formatSignalValue(
        readObjectField(object, [
          'deliveryEvidenceMissing',
          'delivery_evidence_missing',
          'missingDeliveryEvidence',
          'missingDeliveryEvidenceCount',
        ])
      ),
      severity: 'warning',
      visible:
        Boolean(
          readObjectField(object, [
            'deliveryEvidenceMissing',
            'delivery_evidence_missing',
            'missingDeliveryEvidence',
          ])
        ) ||
        Number(
          readObjectField(object, [
            'missingDeliveryEvidenceCount',
            'missing_delivery_evidence_count',
          ]) ?? 0
        ) > 0,
    },
  ];
}

function buildAdminSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'permission_issues',
      label: '권한 이슈',
      value: formatSignalValue(readObjectField(object, ['permissionIssues', 'permission_issues'])),
      severity: 'warning',
      visible: Boolean(readObjectField(object, ['permissionIssues', 'permission_issues'])),
    },
    {
      id: 'audit_flags',
      label: '감사 플래그',
      value: formatSignalValue(readObjectField(object, ['auditFlags', 'audit_flags'])),
      severity: 'danger',
      visible: Boolean(readObjectField(object, ['auditFlags', 'audit_flags'])),
    },
    {
      id: 'schema_changes',
      label: '스키마 변경',
      value: formatSignalValue(readObjectField(object, ['schemaChanges', 'schema_changes'])),
      severity: 'info',
      visible: Boolean(readObjectField(object, ['schemaChanges', 'schema_changes'])),
    },
  ];
}

function buildOwnerSignals(object: Record<string, unknown>): InspectorSignal[] {
  return [
    {
      id: 'credit_hold',
      label: '신용 보류',
      value: formatSignalValue(readObjectField(object, ['creditHold', 'credit_hold'])),
      severity: 'danger',
      visible: Boolean(readObjectField(object, ['creditHold', 'credit_hold'])),
    },
    {
      id: 'overdue_assignments',
      label: '기한 초과 배정',
      value: formatSignalValue(
        readObjectField(object, ['overdueAssignments', 'overdue_assignments'])
      ),
      severity: 'danger',
      visible:
        Number(readObjectField(object, ['overdueAssignments', 'overdue_assignments']) ?? 0) > 0,
    },
    {
      id: 'audit_flags',
      label: '감사 플래그',
      value: formatSignalValue(readObjectField(object, ['auditFlags', 'audit_flags'])),
      severity: 'danger',
      visible: Boolean(readObjectField(object, ['auditFlags', 'audit_flags'])),
    },
  ];
}

function buildOperationsSignals(object: Record<string, unknown>): InspectorSignal[] {
  const missingEvidenceCount = readObjectField(object, [
    'missingEvidenceTasks',
    'missing_evidence_tasks',
    'missingEvidenceTaskCount',
  ]);
  const overdueCount = readObjectField(object, ['overdueAssignments', 'overdue_assignments']);

  return [
    {
      id: 'missing_evidence_tasks',
      label: '증빙 누락 작업',
      value: formatSignalValue(missingEvidenceCount),
      severity: 'warning',
      actionId: 'task:complete',
      visible:
        Number(missingEvidenceCount ?? 0) > 0 ||
        Boolean(readObjectField(object, ['missingEvidence', 'missing_evidence'])),
    },
    {
      id: 'overdue_assignments',
      label: '기한 초과 배정',
      value: formatSignalValue(overdueCount),
      severity: 'danger',
      visible: Number(overdueCount ?? 0) > 0,
    },
    {
      id: 'stock_link',
      label: '재고 연결',
      value: formatSignalValue(readObjectField(object, ['stockLink', 'stock_link'])),
      severity: 'info',
      visible: readObjectField(object, ['stockLink', 'stock_link']) !== undefined,
    },
  ];
}

function resolveRoleSpecificSignals(input: {
  role: WorkbenchRole;
  domain: string;
  objectType: string;
  object: Record<string, unknown>;
}): InspectorSignal[] {
  const domain = input.domain.trim().toLowerCase();
  const objectType = input.objectType.trim().toLowerCase();

  if (input.role === 'sales' && (domain === 'customer' || domain === 'entity')) {
    return buildSalesCustomerSignals(input.object);
  }
  if (
    input.role === 'sales' &&
    (domain === 'order' || objectType === 'order' || domain === 'sales_order')
  ) {
    return buildSalesOrderSignals(input.object);
  }
  if (input.role === 'finance' && (domain === 'customer' || domain === 'finance')) {
    return buildFinanceCustomerSignals(input.object);
  }
  if (
    input.role === 'finance' &&
    (domain === 'order' || objectType === 'order' || domain === 'sales_order')
  ) {
    return buildFinanceOrderSignals(input.object);
  }
  if (
    input.role === 'warehouse' &&
    (domain === 'order' || objectType === 'order' || domain === 'warehouse_picking_list')
  ) {
    return buildWarehouseOrderSignals(input.object);
  }
  if (
    input.role === 'warehouse' &&
    (domain === 'inventory' ||
      domain === 'sellable_item' ||
      domain === 'item' ||
      objectType === 'sellable')
  ) {
    return buildWarehouseInventorySignals(input.object);
  }
  if (input.role === 'owner') {
    return buildOwnerSignals(input.object);
  }
  if (input.role === 'admin') {
    return buildAdminSignals(input.object);
  }
  if (input.role === 'operations') {
    const operationsSignals = buildOperationsSignals(input.object);
    if (domain === 'order' || objectType === 'order' || domain === 'warehouse_picking_list') {
      return mergeRoleSignalsById(operationsSignals, buildWarehouseOrderSignals(input.object));
    }
    if (
      domain === 'inventory' ||
      domain === 'sellable_item' ||
      domain === 'item' ||
      domain === 'inventory_shortage' ||
      objectType === 'sellable'
    ) {
      return mergeRoleSignalsById(operationsSignals, buildWarehouseInventorySignals(input.object));
    }
    return operationsSignals;
  }
  return [];
}

function sortSignalsByRolePriority(
  role: WorkbenchRole,
  signals: InspectorSignal[],
  signalPriorityOverride?: readonly string[]
): InspectorSignal[] {
  const priorities = signalPriorityOverride ?? ROLE_SIGNAL_PRIORITY[role] ?? [];
  const rank = new Map(priorities.map((id, index) => [id, index]));

  return [...signals].sort((left, right) => {
    const leftRank = rank.get(left.id) ?? priorities.length;
    const rightRank = rank.get(right.id) ?? priorities.length;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.label.localeCompare(right.label, 'ko');
  });
}

export function resolveRoleInspectorSignals(input: {
  role: WorkbenchRole;
  domain: string;
  objectType: string;
  object: Record<string, unknown>;
  signalPriority?: readonly string[];
}): InspectorSignal[] {
  const roleSignals = resolveRoleSpecificSignals(input);
  return sortSignalsByRolePriority(input.role, roleSignals, input.signalPriority).filter(
    (signal) => signal.visible
  );
}

export function mergeTenantRoleConfig(
  role: WorkbenchRole,
  tenantPartial: TenantRoleConfigPartial | null | undefined
): { navigator: RoleNavigatorConfig; preset: ResolvedRoleWorkbenchPreset } {
  const navigator = resolveRoleNavigatorConfig(role, tenantPartial?.navigator);
  const preset = applyRoleWorkbenchPresetLayers({
    role,
    ...(tenantPartial?.preset ? { workspace: tenantPartial.preset } : {}),
  });
  return { navigator, preset };
}
